/* global React, Icon, Button, Field, Input, Select, toast */
// Onboarding uses PlummeBackend for business creation. Keep Firebase calls in services/backend.jsx.
function getOnboardingPageGroups() {
  const nav = (typeof window !== 'undefined' && window.PLUMME_NAV) || [];
  const locked = (typeof window !== 'undefined' && window.PAGE_LOCKED) || {};
  const rule = (typeof window !== 'undefined' && window.PAGE_RULE) || {};
  const hideable = (item) => !locked[item.key] && !rule[item.key];
  return nav
    .map((group) => ({ ...group, items: (group.items || []).filter(hideable) }))
    .filter((group) => group.items.length);
}
function onboardingInitials(name) {
  return String(name || '?').split(/\s+/).filter(Boolean).slice(0, 2).map((part) => part[0]).join('').toUpperCase() || '?';
}
function OnboardingDialog({ onClose, mode, inviteId = '' }) {
  const backend = window.PlummeBackend ? window.PlummeBackend.useBackend() : { user: null, memberships: [] };
  const [kind, setKind] = React.useState(mode === 'foretak' || mode === 'privat' ? mode : null);
  const [step, setStep] = React.useState(0);
  const [busy, setBusy] = React.useState(false);
  const [createError, setCreateError] = React.useState('');
  const [selectedRegistryOrg, setSelectedRegistryOrg] = React.useState('');
  const [availability, setAvailability] = React.useState(null);
  const [availabilityBusy, setAvailabilityBusy] = React.useState(false);
  const [createdBusiness, setCreatedBusiness] = React.useState(null);
  const [privateModes, setPrivateModes] = React.useState(() => (inviteId ? ['personlig', 'familie'] : ['personlig']));
  const [registryBusy, setRegistryBusy] = React.useState(false);
  const [registryMatches, setRegistryMatches] = React.useState([]);
  const [addressBusy, setAddressBusy] = React.useState(false);
  const [addressMatches, setAddressMatches] = React.useState([]);
  const suppressAddressLookup = React.useRef(false);
  const [data, setData] = React.useState({
    businessName: '',
    organizationNumber: '',
    address: '',
    postalCode: '',
    postalPlace: '',
    companyTypeCode: 'AS',
    companyTypeLabel: 'Aksjeselskap',
    vatRegistered: null,
    registryVatRegistered: null,
    // '' = unanswered, 'before' = before Plumme's first accounting year,
    // 'dated' = within the accounting period and therefore requires an exact date.
    vatRegisteredAnswer: '',
    vatRegisteredYear: '',
    vatRegisteredFrom: '',
    simplifiedEnkSetup: null,
    hasBusinessBankAccount: null, companyRestModeRequested: null,
    country: 'NO',
    startupSource: '', accountingStartYear: new Date().getFullYear(),
    ...window.PlummeOnboardingBusiness.defaultFields(),
    pagesOff: {},
  });
  const set = (patch) => setData((current) => ({ ...current, ...patch }));
  const user = backend.user || {};
  const userName = user.displayName || user.email || 'Innlogget bruker';
  const userEmail = user.email || '';
  const cleanOrg = String(data.organizationNumber || '').replace(/\D/g, '');
  const membershipOrgNumbers = React.useMemo(() => new Set((backend.memberships || [])
    .map((membership) => String(membership.organizationNumber || membership.orgnr || '').replace(/\D/g, ''))
    .filter(Boolean)), [backend.memberships]);
  const existingMembership = (backend.memberships || []).find((membership) => {
    const org = String(membership.organizationNumber || membership.orgnr || '').replace(/\D/g, '');
    return cleanOrg.length === 9 && org === cleanOrg;
  });
  const companyTypeOptions = React.useMemo(() => window.PlummeBusinessRegistry.companyTypeOptions(data), [data.companyTypeCode, data.companyTypeLabel]);
  const companyTypeLocked = data.country === 'NO' && !!selectedRegistryOrg && !!data.companyTypeCode;
  const isRegistryEnk = companyTypeLocked && data.companyTypeCode === 'ENK', isRegistryAs = companyTypeLocked && data.companyTypeCode === 'AS'; const [restModeModuleReady, setRestModeModuleReady] = React.useState(() => !!window.CompanyRestModeStage);
  React.useEffect(() => { if (!isRegistryAs || window.CompanyRestModeStage) return; let mounted = true; window.PlummePageLoader?.loadRoute('company-rest-mode').then(() => { if (mounted) setRestModeModuleReady(true); }); return () => { mounted = false; }; }, [isRegistryAs]); const businessStageKinds = window.PlummeOnboardingEnk.stageKinds(isRegistryEnk, isRegistryAs, data);
  const currentStageKind = kind === 'foretak' ? businessStageKinds[step] : '';
  const duplicateMessage = existingMembership
    ? t('onb.ui.alreadyLinked')
    : (!availability?.available && availability?.message ? availability.message : '');
  const updateCompanyType = (code) => {
    const option = window.PlummeBusinessRegistry.companyTypeOptions(data).find(([value]) => value === code);
    set({ companyTypeCode: code, companyTypeLabel: option ? option[1].replace(/\s+\([^)]+\)$/, '') : data.companyTypeLabel });
  };
  const pageGroups = React.useMemo(() => getOnboardingPageGroups(), []);
  const togglePage = (key) => setData((current) => {
    const pagesOff = { ...(current.pagesOff || {}) };
    if (pagesOff[key]) delete pagesOff[key];
    else pagesOff[key] = true;
    return { ...current, pagesOff };
  });
  const hiddenPages = data.pagesOff || {};
  const accountingStartYear = Number(data.accountingStartYear);
  const selectedPageKeys = React.useMemo(
    () => pageGroups.flatMap((group) => group.items || []).filter((item) => !hiddenPages[item.key]).map((item) => item.key),
    [pageGroups, hiddenPages],
  );
  const finishCreatedBusiness = () => { const businessId = createdBusiness?.businessId || createdBusiness?.id; if (businessId) { window.PlummeBackend?.setActiveBusinessId?.(businessId); window.dispatchEvent(new CustomEvent('plumme-business-created', { detail: { businessId, year: accountingStartYear } })); } onClose(); };
  const resetBusinessLookup = (countryOverride) => {
    const country = typeof countryOverride === 'string' ? countryOverride : data.country;
    setSelectedRegistryOrg('');
    setRegistryMatches([]);
    setAddressMatches([]);
    setAvailability(null);
    setCreateError('');
    set({
      businessName: '',
      organizationNumber: '',
      address: '',
      postalCode: '',
      postalPlace: '',
      country, companyTypeCode: country === 'NO' ? 'AS' : '',
      companyTypeLabel: country === 'NO' ? 'Aksjeselskap' : '',
      vatRegistered: null,
      registryVatRegistered: null,
      vatRegisteredAnswer: '',
      vatRegisteredYear: '',
      vatRegisteredFrom: '',
      simplifiedEnkSetup: null,
      hasBusinessBankAccount: null, companyRestModeRequested: null,
      ...window.PlummeOnboardingBusiness.defaultFields(),
      pagesOff: {},
    });
  };
  const next = () => {
    if (kind === 'foretak' && currentStageKind === 'company' && availabilityBusy) return;
    if (kind === 'foretak' && currentStageKind === 'company' && !String(data.companyTypeCode || '').trim()) { setCreateError(t('onb.companyFormRequired')); return; }
    if (kind === 'foretak' && currentStageKind === 'company' && duplicateMessage) {
      setCreateError(duplicateMessage);
      return;
    }
    if (kind === 'foretak' && currentStageKind === 'startup' && !data.startupSource) { setCreateError(t('onb.startup.required', 'Velg hvordan regnskapet starter i Plumme.')); return; }
    if (kind === 'foretak' && currentStageKind === 'startup' && !window.onboardingStartupYearIsValid(accountingStartYear)) { setCreateError(t('onb.startup.startYearInvalid', 'Velg et gyldig oppstartsår som ikke er frem i tid.')); return; }
    if (kind === 'foretak' && currentStageKind === 'startup' && typeof data.vatRegistered !== 'boolean') { setCreateError(t('onb.vat.statusRequired', 'Velg om foretaket er MVA-registrert.')); return; }
    if (kind === 'foretak' && currentStageKind === 'startup' && data.vatRegistered === true && !data.vatRegisteredAnswer) { setCreateError(t('onb.vat.timingRequired', 'Oppgi når foretaket ble MVA-registrert.')); return; }
    if (kind === 'foretak' && currentStageKind === 'startup' && data.vatRegistered === true && data.vatRegisteredAnswer === 'before' && !(Number.isInteger(Number(data.vatRegisteredYear)) && Number(data.vatRegisteredYear) >= 1900 && Number(data.vatRegisteredYear) < accountingStartYear)) { setCreateError(t('onb.vat.yearInvalid', 'Registreringsåret må være før første regnskapsår i Plumme.')); return; }
    if (kind === 'foretak' && currentStageKind === 'startup' && data.vatRegistered === true && data.vatRegisteredAnswer === 'dated' && !/^\d{4}-\d{2}-\d{2}$/.test(String(data.vatRegisteredFrom || ''))) { setCreateError(t('onb.vat.dateRequired', 'Legg inn den nøyaktige registreringsdatoen.')); return; }
    if (kind === 'foretak' && currentStageKind === 'startup' && data.vatRegistered === true && data.vatRegisteredAnswer === 'dated' && String(data.vatRegisteredFrom) < `${accountingStartYear}-01-01`) { setCreateError(t('onb.vat.dateBeforeStart', 'Bruk registreringsår når registreringen skjedde før første regnskapsår i Plumme.')); return; }
    if (kind === 'foretak' && currentStageKind === 'startup' && data.vatRegistered === true && data.vatRegisteredAnswer === 'dated' && String(data.vatRegisteredFrom) > new Date().toISOString().slice(0, 10)) { setCreateError(t('onb.vat.dateFuture', 'Registreringsdatoen kan ikke være frem i tid.')); return; }
    if (kind === 'foretak' && currentStageKind === 'enkSetup') {
      const enkError = window.PlummeOnboardingEnk.validate(data);if (enkError) { setCreateError(enkError); return; }
      set({ pagesOff: data.simplifiedEnkSetup ? window.PlummeOnboardingEnk.hiddenPages(data) : {} });
    } if (kind === 'foretak' && currentStageKind === 'restMode') { if (typeof data.companyRestModeRequested !== 'boolean') { setCreateError(t('onb.restMode.required')); return; } set({ pagesOff: data.companyRestModeRequested ? window.PlummeCompanyRestMode.hiddenPages() : {} }); }
    if (kind === 'foretak') {
      const setupError = window.PlummeOnboardingBusiness.validateStage(currentStageKind, data);
      if (setupError) { setCreateError(setupError); return; }
    }
    if (kind === 'foretak' && currentStageKind === 'bank' && isRegistryEnk && data.simplifiedEnkSetup === true) set({ pagesOff: window.PlummeOnboardingEnk.hiddenPages(data) });
    setCreateError(''); setStep((current) => Math.min(kind === 'privat' ? 3 : businessStageKinds.length - 1, current + 1));
  };
  const back = () => {
    if (step === 0 && !mode) {
      setKind(null);
      return;
    }
    setStep((current) => Math.max(0, current - 1));
  };
  const createBusiness = async () => {
    setCreateError('');
    if (kind === 'privat') {
      setBusy(true);
      try {
        if (!window.PlummeBackend?.createPersonalFinanceClient) throw new Error(t('onb.ui.personalNotConnected'));
        const selectedUses = privateModes.map((mode) => `privat-${mode}`);
        const familyEnabled = selectedUses.includes('privat-familie');
        const business = await window.PlummeBackend.createPersonalFinanceClient({
          selectedUses,
          source: inviteId ? 'plumme-household-invite' : 'plumme-private-onboarding',
        });
        const businessId = business?.businessId || business?.id;
        window.PlummeBackend?.updateState?.((state) => ({
          ...state,
          settings: {
            ...(state.settings || {}),
            onboardingPreferences: {
              ...((state.settings || {}).onboardingPreferences || {}),
              selectedUses,
            },
            personalFinanceFeatures: {
              ...((state.settings || {}).personalFinanceFeatures || {}),
              family: familyEnabled,
            },
          },
        }), true);
        if (inviteId && businessId) {
          try {
            await window.PlummeBackend.respondPersonalFinanceHouseholdInvite({ action: 'accept', businessId, inviteId });
            window.PlummeHouseholdInvite?.clear?.(inviteId);
            toast(t('onb.ui.personalCreatedLinked'), 'success');
          } catch (inviteError) {
            toast(inviteError?.message || t('onb.ui.personalCreatedInvitePending'), 'warning');
          }
        } else {
          toast(t('onb.ui.personalCreated'), 'success');
        }
        window.dispatchEvent(new CustomEvent('plumme-private-created', { detail: { businessId } }));
        onClose();
      } catch (error) {
        toast(error?.message || t('onb.ui.personalCreateFailed'), 'error');
      } finally {
        setBusy(false);
      }
      return;
    }
    const org = cleanOrg;
    // VAT is a required accounting-start fact. Return to that step instead of
    // creating a company with an unresolved registration boundary.
    if (typeof data.vatRegistered !== 'boolean' || (data.vatRegistered && !data.vatRegisteredAnswer)) {
      toast(t('onb.vat.incomplete', 'Fullfør MVA-opplysningene.'), 'error');
      setStep(2);
      return;
    }
    if (data.vatRegistered === true && data.vatRegisteredAnswer === 'dated'
      && !/^\d{4}-\d{2}-\d{2}$/.test(String(data.vatRegisteredFrom || ''))) {
      toast(t('onb.vat.dateRequired', 'Legg inn den nøyaktige registreringsdatoen.'), 'error');
      setStep(2);
      return;
    }
    if (data.country === 'NO' && org.length !== 9) {
      toast(t('onb.ui.orgNumberNineDigits'), 'error');
      setStep(1);
      return;
    }
    if (existingMembership) {
      setCreateError(duplicateMessage);
      setStep(1);
      return;
    }
    if (availability && !availability.available) {
      setCreateError(availability.message || t('onb.ui.alreadyExists'));
      setStep(1);
      return;
    }
    setBusy(true);
    try {
      if (!window.PlummeBackend?.createBusiness) throw new Error(t('onb.ui.createNotConnected'));
      const business = await window.PlummeBackend.createBusiness({
        country: data.country,
        businessName: data.businessName,
        organizationNumber: org || data.organizationNumber,
        address: [data.address, data.postalCode, data.postalPlace].filter(Boolean).join(', '),
        companyTypeCode: data.companyTypeCode,
        companyTypeLabel: data.companyTypeLabel,
        vatRegistered: data.vatRegistered,
        ...window.PlummeOnboardingEnk.vatRegistrationPayload(data),
        onboardingPreferences: {
          selectedUses: selectedPageKeys,
          selectedPages: selectedPageKeys,
          hiddenPages,
          menuPreset: data.companyRestModeRequested === true ? 'company_rest_mode' : data.simplifiedEnkSetup === true ? 'simple_sole_trader' : 'custom',
          hasBusinessBankAccount: data.simplifiedEnkSetup === true ? data.bankStatus === 'complete' : null,
          source: 'plumme-onboarding',
        },
        ...window.PlummeOnboardingBusiness.createPayload(data, accountingStartYear), ...(data.companyRestModeRequested === true ? { companyRestMode: { requested: true } } : {}),
        deferActivation: true,
      });
      try { await window.PlummeOnboardingEnk.persistHiddenPages(hiddenPages); } catch (preferenceError) { toast(t('onb.menuSaveWarning', 'Foretaket ble opprettet, men menyvalget kunne ikke lagres til profilen din.'), 'warning'); }
      setCreatedBusiness(business);
      setStep(businessStageKinds.indexOf('done'));
      toast(t('onb.ui.companyCreated'), 'success');
    } catch (error) {
      const message = error?.message || t('onb.ui.companyCreateFailed');
      setCreateError(message);
      toast(message, 'error');
    } finally {
      setBusy(false);
    }
  };

  const applyRegistryMatch = (match) => {
    if (!match) return;
    if (match.registeredMessage) {
      setCreateError(match.registeredMessage);
      return;
    }
    suppressAddressLookup.current = true;
    setSelectedRegistryOrg(String(match.organizationNumber || '').replace(/\D/g, ''));
    setAvailability(null);
    setCreateError('');
    set({
      businessName: match.businessName || match.name || data.businessName,
      organizationNumber: match.organizationNumber || data.organizationNumber,
      address: match.address || data.address,
      postalCode: match.postalCode || data.postalCode,
      postalPlace: match.postalPlace || data.postalPlace,
      companyTypeCode: match.companyTypeCode || data.companyTypeCode,
      companyTypeLabel: match.companyTypeLabel || data.companyTypeLabel,
      registryVatRegistered: typeof match.vatRegistered === 'boolean' ? match.vatRegistered : null,
      vatRegistered: null,
      // A new company requires a new explicit answer even when the registry
      // provides a current yes/no observation.
      vatRegisteredAnswer: '',
      vatRegisteredYear: '',
      vatRegisteredFrom: '',
      simplifiedEnkSetup: null,
      hasBusinessBankAccount: null, companyRestModeRequested: null,
      ...window.PlummeOnboardingBusiness.defaultFields(),
      pagesOff: {},
    });
    setRegistryMatches([]);
    setAddressMatches([]);
  };

  const applyAddressMatch = (match) => {
    if (!match) return;
    suppressAddressLookup.current = true;
    set({
      address: match.address || data.address,
      postalCode: match.postalCode || data.postalCode,
      postalPlace: match.postalPlace || data.postalPlace,
    });
    setAddressMatches([]);
  };

  React.useEffect(() => {
    if (kind !== 'foretak' || step !== 1 || data.country !== 'NO') {
      setRegistryMatches([]);
      return undefined;
    }
    const org = String(data.organizationNumber || '').replace(/\D/g, '');
    const query = org.length === 9 ? org : data.businessName.trim();
    if (org.length === 9 && org === selectedRegistryOrg) {
      setRegistryMatches([]);
      return undefined;
    }
    if ((org.length > 0 && org.length < 9) || query.length < 2) {
      setRegistryMatches([]);
      return undefined;
    }
    let cancelled = false;
    const timer = window.setTimeout(async () => {
      setRegistryBusy(true);
      try {
        const matches = await window.PlummeBackend?.lookupBusinessRegistry?.(query, data.country);
        const rawMatches = Array.isArray(matches) ? matches : [];
        const enrichedMatches = await Promise.all(rawMatches.map(async (match) => {
          const matchOrg = String(match.organizationNumber || '').replace(/\D/g, '');
          if (matchOrg.length !== 9) return match;
          if (membershipOrgNumbers.has(matchOrg)) {
            return {
              ...match,
              registeredStatus: 'member',
              registeredMessage: t('onb.ui.alreadyLinked'),
            };
          }
          try {
            const result = await window.PlummeBackend?.checkBusinessAvailability?.({ country: data.country, organizationNumber: matchOrg });
            if (result && result.available === false) {
              return {
                ...match,
                registeredStatus: result.alreadyMember ? 'member' : 'taken',
                registeredMessage: result.message || 'Dette foretaket finnes allerede i Plumme.',
              };
            }
          } catch (_error) {
            return match;
          }
          return match;
        }));
        if (!cancelled) setRegistryMatches(enrichedMatches);
      } catch (_error) {
        if (!cancelled) setRegistryMatches([]);
      } finally {
        if (!cancelled) setRegistryBusy(false);
      }
    }, 280);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [kind, step, data.country, data.businessName, data.organizationNumber, selectedRegistryOrg, membershipOrgNumbers]);

  React.useEffect(() => {
    if (kind !== 'foretak' || step !== 1 || data.country !== 'NO' || cleanOrg.length !== 9 || existingMembership) {
      setAvailability(null);
      setAvailabilityBusy(false);
      return undefined;
    }
    let cancelled = false;
    setAvailabilityBusy(true);
    const timer = window.setTimeout(async () => {
      try {
        const result = await window.PlummeBackend?.checkBusinessAvailability?.({ country: data.country, organizationNumber: cleanOrg });
        if (!cancelled) setAvailability(result || null);
      } catch (_error) {
        if (!cancelled) setAvailability(null);
      } finally {
        if (!cancelled) setAvailabilityBusy(false);
      }
    }, 220);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [kind, step, data.country, cleanOrg, !!existingMembership]);

  React.useEffect(() => {
    if (kind !== 'foretak' || step !== 1 || data.country !== 'NO' || data.address.trim().length < 3) {
      setAddressMatches([]);
      return undefined;
    }
    if (suppressAddressLookup.current) {
      suppressAddressLookup.current = false;
      setAddressMatches([]);
      return undefined;
    }
    let cancelled = false;
    const timer = window.setTimeout(async () => {
      setAddressBusy(true);
      try {
        const matches = await window.PlummeBackend?.searchAddressLookup?.(data.address, data.country);
        if (!cancelled) setAddressMatches(Array.isArray(matches) ? matches : []);
      } catch (_error) {
        if (!cancelled) setAddressMatches([]);
      } finally {
        if (!cancelled) setAddressBusy(false);
      }
    }, 260);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [kind, step, data.country, data.address]);

  React.useEffect(() => {
    const onKey = (event) => { if (event.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const plumSrc = (window.__resources && window.__resources.superPlummaHero) || 'src/img/SuperPlumma.png';
  const doneSrc = 'src/img/PlummaCheer.png';

  if (!kind) {
    return <div className="ob-scrim">
      <div className="ob-card" role="dialog" aria-modal="true" aria-label={t('onb.getStartedTitle')}>
        <button className="ob-close" onClick={onClose} aria-label={t('common.close')}><Icon name="x" size={18} /></button>
        <div className="ob-stage" style={{ paddingTop: 34 }}>
          <div className="ob-hero" style={{ marginBottom: 8 }}>
            <img className="ob-hero-plum" src={plumSrc} alt="SuperPlumma" />
            <div className="ob-eyebrow">{t('onb.getStarted')}</div>
            <h2 className="ob-title">{t('onb.whatCreate')}</h2>
            <p className="ob-lead">{t('onb.whatCreateHelp')}</p>
          </div>
          <div className="ob-choose">
            <button type="button" className="ob-choose-card" onClick={() => { setKind('foretak'); setStep(0); }}>
              <span className="ob-choose-ico"><Icon name="building" size={22} /></span>
              <span className="ob-choose-name">{t('set.company')}</span>
              <span className="ob-choose-sub">{t('onb.registerCompany')}</span>
            </button>
            <button type="button" className="ob-choose-card" onClick={() => { setKind('privat'); setStep(0); }}>
              <span className="ob-choose-ico"><Icon name="sparkle" size={22} /></span>
              <span className="ob-choose-name">{t('po.title')}</span>
              <span className="ob-choose-sub">{t('onb.ui.chooseSubPersonal')}</span>
            </button>
            <button type="button" className="ob-choose-card" onClick={() => window.showOnboarding?.('regnskapsforer')}>
              <span className="ob-choose-ico"><Icon name="users" size={22} /></span>
              <span className="ob-choose-name">{t('sharedText.rf.group.firm')}</span>
              <span className="ob-choose-sub">{t('onb.ui.chooseSubFirm')}</span>
            </button>
          </div>
        </div>
        <div className="ob-nav"><span /><button className="ob-skip" onClick={onClose}>{t('common.cancel')}</button></div>
      </div>
    </div>;
  }

  const stages = kind === 'privat'
    ? window.buildPersonalFinanceOnboardingStages({ doneSrc, inviteId, plumSrc, privateModes, setPrivateModes })
    : [
        <div className="ob-hero" key="0">
          <img className="ob-hero-plum" src={plumSrc} alt="SuperPlumma" />
          <div className="ob-eyebrow">{t('onb.welcome')}</div>
          <h2 className="ob-title">{t('onb.letsSetUp')}</h2>
          <p className="ob-lead">{t('onb.ui.leadVerify')}</p>
        </div>,
        <div key="1">
          <div className="ob-eyebrow">{t('onb.ui.step1Company')}</div>
          <h2 className="ob-title">{t('onb.tellUsAbout')}</h2>
          {userName && <div className="ob-userbar">
            <span className="ob-userbar-ava">{onboardingInitials(userName)}</span>
            <span className="ob-userbar-meta"><span className="ob-userbar-name">{userName}</span><span className="ob-userbar-email">{userEmail}</span></span>
            <span className="ob-userbar-tag">{t('onb.signedIn')}</span>
          </div>}
          <div className="ob-fields">
            <Field label={t('set.country')}><Select value={data.country} onChange={(event) => resetBusinessLookup(event.target.value)}>
              <option value="NO">{t('pay.norway')}</option>
              <option value="SE">{t('onb.sweden')}</option>
              <option value="DK">{t('onb.denmark')}</option>
            </Select></Field>
            <Field label={t('set.companyName')}><Input value={data.businessName} onChange={(event) => { setSelectedRegistryOrg(''); setAvailability(null); set({ businessName: event.target.value }); }} placeholder={t('onb.companyNamePh')} /></Field>
            <Field label={t('set.orgNumber')}><Input value={data.organizationNumber} onChange={(event) => { setSelectedRegistryOrg(''); setAvailability(null); setCreateError(''); set({ organizationNumber: event.target.value }); }} placeholder={t('onb.nineDigits')} inputMode="numeric" /></Field>
            {(data.businessName || data.organizationNumber) && <div className="row" style={{ justifyContent: 'flex-end', marginTop: -8 }}><button type="button" className="ob-reset" onClick={resetBusinessLookup}>{t('onb.resetAndSearch')}</button></div>}
            {availabilityBusy && <div className="ob-lookup-note">{t('onb.checkingExisting')}</div>}
            {duplicateMessage && <div className="ob-warning"><Icon name="info" size={17} /><span>{duplicateMessage}</span></div>}
            {createError && <div className="ob-warning ob-warning-error"><Icon name="alertTri" size={17} /><span>{createError}</span></div>}
            {(registryBusy || registryMatches.length > 0) && <div className="ob-lookup">
              {registryBusy && <div className="ob-lookup-note">{t('onb.searchingRegistry')}</div>}
              {registryMatches.map((match) => {
                const registered = !!match.registeredMessage;
                return <button
                  type="button"
                  key={match.organizationNumber}
                  disabled={registered}
                  className={`ob-lookup-row${registered ? ' is-disabled' : ''}`}
                  title={registered ? match.registeredMessage : undefined}
                  onClick={() => applyRegistryMatch(match)}
                >
                  <span>
                    <b>{match.businessName || match.name}</b>
                    <small>{match.organizationNumber} · {match.companyTypeCode || 'Foretak'}</small>
                    {registered && <em>{match.registeredStatus === 'member' ? 'Allerede koblet til brukeren din' : 'Allerede registrert i Plumme'}</em>}
                  </span>
                  {registered ? <Icon name="info" size={16} /> : <Icon name="arrowRight" size={16} />}
                </button>;
              })}
            </div>}
            <Field label={t('onb.companyForm')}>{data.country !== 'NO' ? <Input value={data.companyTypeCode} placeholder={t('onb.companyForm')} onChange={(event) => set({ companyTypeCode: event.target.value, companyTypeLabel: event.target.value })} /> : <Select value={data.companyTypeCode} disabled={companyTypeLocked} onChange={(event) => updateCompanyType(event.target.value)}>
              {companyTypeOptions.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
            </Select>}</Field>
            <Field label={t('cust.form.address')}><Input value={data.address} onChange={(event) => { suppressAddressLookup.current = false; set({ address: event.target.value }); }} placeholder={t('cust.form.addressPh')} /></Field>
            {(addressBusy || addressMatches.length > 0) && <div className="ob-lookup">
              {addressBusy && <div className="ob-lookup-note">{t('onb.searchingAddress')}</div>}
              {addressMatches.map((match) => <button type="button" key={match.id} className="ob-lookup-row" onClick={() => applyAddressMatch(match)}>
                <span><b>{match.address}</b><small>{match.postalCode} {match.postalPlace}</small></span>
                <Icon name="arrowRight" size={16} />
              </button>)}
            </div>}
            <div className="grid-2 gap-14">
              <Field label={t('pay.postalCode')}><Input value={data.postalCode} onChange={(event) => set({ postalCode: event.target.value })} placeholder="0000" inputMode="numeric" /></Field>
              <Field label={t('cust.form.city')}><Input value={data.postalPlace} onChange={(event) => set({ postalPlace: event.target.value })} placeholder={t('onb.oslo')} /></Field>
            </div>
          </div>
        </div>,
        <window.OnboardingStartupStage key="2" data={data} set={set} createError={createError} />,
        ...(data.startupSource === 'previous_system' ? [<window.OnboardingMigrationStage key="migration" data={data} set={set} createError={createError} />, <window.OnboardingOpeningAssetsStage key="assets" data={data} set={set} createError={createError} />] : []),
        ...(isRegistryEnk ? [<window.EnkSimplifiedSetupStage key="enk-setup" data={data} set={set} createError={createError} />] : []), ...(isRegistryAs ? [restModeModuleReady && window.CompanyRestModeStage ? React.createElement(window.CompanyRestModeStage, { key: 'rest-mode', data, set, createError }) : <div key="rest-mode" className="ob-lead">{t('app.loading')}</div>] : []),
        <window.OnboardingBankSetupStage key="bank" data={data} set={set} createError={createError} />,
         <window.OnboardingPageSelectionStage key="pages" pageGroups={pageGroups} hiddenPages={hiddenPages} togglePage={togglePage} isRegistryEnk={isRegistryEnk} />,
        <window.OnboardingInvoiceSetupStage key="invoice" data={data} set={set} createError={createError} />,
        <window.OnboardingVehicleStage key="vehicle" data={data} set={set} createError={createError} />,
        <window.OnboardingReviewStage key="review" data={data} createError={createError} />,
        <window.OnboardingBusinessDoneStage key="done" doneSrc={doneSrc} createdBusiness={createdBusiness} businessName={data.businessName} />,
      ];
  return <div className="ob-scrim">
    <div className="ob-card" role="dialog" aria-modal="true" aria-label={t('onb.getStartedTitle')}>
      <button className="ob-close" onClick={currentStageKind === 'done' && createdBusiness ? finishCreatedBusiness : onClose} aria-label={t('common.close')}><Icon name="x" size={18} /></button>
      <div className="ob-progress">{Array.from({ length: kind === 'privat' ? 4 : businessStageKinds.length }, (_, i) => <span key={i} className={'ob-dot' + (i === step ? ' on' : i < step ? ' done' : '')} />)}</div>
      <div className="ob-stage">{stages[step]}</div>
      <div className="ob-nav">
        {step === 0 ? <button className="ob-skip" onClick={back}>{t('onb.ui.back')}</button> : <button className="ob-skip" onClick={back}>{t('onb.ui.back')}</button>}
        {kind === 'privat' && step === 3
          ? <Button className="btn-lg" icon="check" disabled={busy} onClick={createBusiness} style={{ marginLeft: 'auto' }}>{busy ? t('onb.ui.creating') : inviteId ? t('onb.ui.createAndAccept') : t('onb.ui.createPersonal')}</Button>
          : currentStageKind === 'done'
          ? <Button className="btn-lg" icon="arrowUpRight" onClick={finishCreatedBusiness}>{t('onb.goToPlumme')}</Button>
          : currentStageKind === 'review'
            ? <Button className="btn-lg" icon="check" disabled={busy} onClick={createBusiness} style={{ marginLeft: 'auto' }}>{busy ? t('onb.ui.creating') : t('onb.ui.createCompany')}</Button>
            : <Button className="btn-lg" icon="arrowRight" disabled={currentStageKind === 'company' && availabilityBusy} onClick={next} style={{ marginLeft: 'auto' }}>{step === 0 ? t('onb.ui.getStarted') : t('onb.ui.next')}</Button>}
      </div>
    </div>
  </div>;
}

function OnboardingLayer() {
  const [info, setInfo] = React.useState({ mode: undefined, inviteId: '' });
  const [open, setOpen] = React.useState(false);
  React.useEffect(() => {
    const handler = async (event) => {
      const detail = event.detail;
      try {
        await window.PlummePageLoader?.loadRoute('onboarding');
        setInfo({ mode: typeof detail === 'string' ? detail : detail?.mode, inviteId: typeof detail === 'object' ? detail?.inviteId || '' : '' });setOpen(true);
      } catch (error) { toast(error?.message || t('onb.loadError'), 'error'); }
    };
    window.addEventListener('plumme-show-onboarding', handler);
    window.showOnboarding = (mode, options) => window.dispatchEvent(new CustomEvent('plumme-show-onboarding', { detail: { mode, ...(options || {}) } }));
    return () => window.removeEventListener('plumme-show-onboarding', handler);
  }, []);
  if (!open) return null;
  if (info.mode === 'byra' && window.FirmOnboardingDialog) {
    return <window.FirmOnboardingDialog onClose={() => setOpen(false)} />;
  }
  if (info.mode === 'regnskapsforer' && window.AccountantOnboardingDialog) {
    return <window.AccountantOnboardingDialog inviteId={info.inviteId} onClose={() => setOpen(false)} />;
  }
  return <OnboardingDialog mode={info.mode} inviteId={info.inviteId} onClose={() => setOpen(false)} />;
}

Object.assign(window, { OnboardingDialog, OnboardingLayer });
